import { useState, type FormEvent } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { ArrowRight, BadgeCheck, BriefcaseBusiness, CheckCircle2, Loader2, LogIn, ShieldCheck, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@zaogig/ui/button';
import { Input } from '@zaogig/ui/input';
import { useAuth, wechatLogin } from '@zaogig/auth';
import type { PhoneLoginParams } from '@zaogig/types';

type EntryType = 'worker' | 'employer';
type Step = 'role-select' | 'login-form';

interface LoginPageProps {
  onWorkerEntry?: () => void;
  onEnterpriseEntry?: () => void;
  onLegalEntry?: () => void;
  onBack?: () => void;
  initialEntry?: EntryType;
}

export default function LoginPage({
  onWorkerEntry,
  onEnterpriseEntry,
  onLegalEntry,
  onBack,
  initialEntry = 'worker',
}: LoginPageProps) {
  const [step, setStep] = useState<Step>('role-select');
  const [selectedEntry, setSelectedEntry] = useState<EntryType>(initialEntry);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loginError, setLoginError] = useState('');
  const { loading, login } = useAuth();
  void wechatLogin; // keep for future button onClick

  const handleRoleSelect = (entry: EntryType) => {
    setSelectedEntry(entry);
    setStep('login-form');
    setLoginError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!phone.trim()) {
      setLoginError('请输入手机号');
      return;
    }
    if (!code.trim()) {
      setLoginError('请输入验证码');
      return;
    }

    const credentials: PhoneLoginParams = {
      phone: phone.trim(),
      code: code.trim(),
      selectedRole: selectedEntry,
    };

    const result = await login(credentials);
    if (!result) {
      setLoginError('登录失败，请检查手机号和验证码');
      return;
    }

    // 登录成功 → 跳转对应入口
    if (result.current_role === 'worker') {
      onWorkerEntry?.();
    } else {
      onEnterpriseEntry?.();
    }
  };

  const handleBackToRole = () => {
    setStep('role-select');
    setLoginError('');
    setPhone('');
    setCode('');
  };

  const openLegal = (label: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onLegalEntry?.();
    toast.info(`已打开协议签署中心：${label}`);
  };

  const isEnterprise = selectedEntry === 'employer';
  const accent = isEnterprise ? '#2563EB' : '#10B981';
  const buttonClass = isEnterprise
    ? 'bg-gradient-to-r from-[#2563EB] to-blue-700 hover:from-blue-600 hover:to-blue-800 shadow-blue-500/30'
    : 'bg-gradient-to-r from-[#10B981] to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/30';

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-emerald-50/50 via-white to-blue-50/50 text-slate-950">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-15rem] top-[-15rem] h-[30rem] w-[30rem] rounded-full bg-emerald-200/40 blur-[6rem]" />
        <div className="absolute right-[-12rem] top-1/4 h-[28rem] w-[28rem] rounded-full bg-blue-200/40 blur-[6rem]" />
      </div>

      <main className="relative mx-auto flex min-h-screen w-full max-w-4xl flex-col items-center px-5 py-8 sm:px-8 lg:px-10">
        <button
          type="button"
          onClick={step === 'login-form' ? handleBackToRole : onBack}
          className="self-start text-slate-500 transition hover:text-slate-700"
        >
          {step === 'login-form' ? '← 返回选择' : '返回首页'}
        </button>

        <div className="mt-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20">
          <ShieldCheck className="h-6 w-6" />
        </div>

        {step === 'role-select' ? (
          <>
            <div className="mt-6 text-center">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white/70 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm shadow-slate-900/5 backdrop-blur">
                <BadgeCheck className="h-4 w-4" />
                选择您的身份
              </div>
              <h1 className="mt-6 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                你是来找活，还是来招人？
              </h1>
              <p className="mt-3 text-base text-slate-600">
                零工走用户入口，招人走企业入口。
              </p>
            </div>

            <div className="mt-8 grid w-full gap-4 sm:mt-10 lg:grid-cols-2 lg:gap-6">
              <EntryCard
                type="worker"
                selected={selectedEntry === 'worker'}
                accent="#10B981"
                icon={<BriefcaseBusiness className="h-8 w-8" />}
                title="我要找活"
                description="看附近任务，确认排班，打卡上工，收入清楚可查。"
                buttonLabel="手机号登录，马上找活"
                onSelect={() => handleRoleSelect('worker')}
              />
              <EntryCard
                type="employer"
                selected={selectedEntry === 'employer'}
                accent="#2563EB"
                icon={<Store className="h-8 w-8" />}
                title="我要招人"
                description="发任务、选人、排班、到岗和结算都能留痕追踪。"
                buttonLabel="手机号登录，马上招人"
                onSelect={() => handleRoleSelect('employer')}
              />
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 text-center">
              <div
                className="mx-auto inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm shadow-slate-900/5 backdrop-blur"
                style={{ borderColor: accent + '40', color: accent }}
              >
                <BadgeCheck className="h-4 w-4" />
                {isEnterprise ? '企业端登录' : '零工端登录'}
              </div>
              <h1 className="mt-6 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                {isEnterprise ? '企业手机号登录' : '零工手机号登录'}
              </h1>
              <p className="mt-3 text-base text-slate-600">
                测试阶段使用手机号 + 验证码登录
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 w-full max-w-sm space-y-5">
              <div className="space-y-2">
                <label htmlFor="login-phone" className="text-sm font-semibold text-slate-700">
                  手机号
                </label>
                <Input
                  id="login-phone"
                  type="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                  className="h-12 rounded-xl border-slate-200 bg-white/80 text-base backdrop-blur"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="login-code" className="text-sm font-semibold text-slate-700">
                  验证码
                </label>
                <div className="flex gap-2">
                  <Input
                    id="login-code"
                    type="text"
                    placeholder="测试阶段输入 000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={loading}
                    className="h-12 flex-1 rounded-xl border-slate-200 bg-white/80 text-base backdrop-blur"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={loading}
                    className="h-12 rounded-xl border-slate-200 text-sm whitespace-nowrap"
                  >
                    获取验证码
                  </Button>
                </div>
                <p className="text-xs text-slate-400">测试阶段默认验证码: 000000</p>
              </div>

              {loginError && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  {loginError}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className={`w-full h-12 rounded-xl text-base font-bold text-white shadow-xl transition-all duration-200 hover:-translate-y-1 ${buttonClass}`}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    登录中...
                  </>
                ) : (
                  <>
                    登录
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </>
        )}

          {/* 微信登录分割线 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-400">其他登录方式</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              const appId = import.meta.env.VITE_WECHAT_APP_ID || ""
              const redirectUri = encodeURIComponent(import.meta.env.VITE_WECHAT_REDIRECT_URI || window.location.origin + "/oauth/wechat/callback")
              const state = Math.random().toString(36).slice(2)
              window.location.href = "https://open.weixin.qq.com/connect/qrconnect?appid=" + appId + "&redirect_uri=" + redirectUri + "&response_type=code&scope=snsapi_login&state=" + state
            }}
            className="w-full h-12 rounded-xl border-2 border-[#07C160] text-[#07C160] bg-white hover:bg-[#07C160]/5 font-bold text-base transition-all duration-200 hover:-translate-y-1 flex items-center justify-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#07C160">
              <path d="M8.5 11a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm4.5-1.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zM12 2C6.48 2 2 6.03 2 11c0 2.85 1.49 5.51 3.86 7.18-.22.73-.68 1.95-1.86 3.82 0 0 3.5-1.1 5.14-1.82.97.33 2.01.51 3.09.51 2.23 0 4.29-.76 5.91-2.02C20.1 16.83 22 14.15 22 11c0-4.97-4.48-9-10-9z"/>
            </svg>
            微信扫码登录
          </button>

        <div className="mt-8 flex flex-col items-center">
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-500">
            <LogIn className="h-4 w-4" />
            <span>登录即表示同意</span>
            <a href="/legal" className="text-emerald-600 hover:underline" onClick={openLegal('服务协议')}>
              服务协议
            </a>
            <span>和</span>
            <a href="/legal" className="text-emerald-600 hover:underline" onClick={openLegal('隐私政策')}>
              隐私政策
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

interface EntryCardProps {
  type: EntryType;
  selected: boolean;
  accent: string;
  icon: ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  onSelect: () => void;
}

function EntryCard({
  type,
  selected,
  accent,
  icon,
  title,
  description,
  buttonLabel,
  onSelect,
}: EntryCardProps) {
  const isWorker = type === 'worker';
  const glowClass = isWorker ? 'shadow-emerald-500/20' : 'shadow-blue-500/20';
  const softBg = isWorker
    ? 'from-emerald-50 via-white to-teal-50/50'
    : 'from-blue-50 via-white to-sky-50/50';
  const buttonClass = isWorker
    ? 'bg-gradient-to-r from-[#10B981] to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/30'
    : 'bg-gradient-to-r from-[#2563EB] to-blue-700 hover:from-blue-600 hover:to-blue-800 shadow-blue-500/30';

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      className={`group relative min-h-[18rem] overflow-hidden rounded-[32px] border bg-gradient-to-br ${softBg} p-5 text-left shadow-xl transition-all duration-300 hover:-translate-y-1.5 sm:p-6 ${
        selected ? `border-white ${glowClass} ring-2 ring-offset-2` : 'border-white/60 shadow-slate-900/8'
      }`}
      style={{ ['--tw-ring-color' as string]: selected ? accent : 'transparent' }}
      aria-pressed={selected}
    >
      <div
        className="absolute right-[-4rem] top-[-4rem] h-[16rem] w-[16rem] rounded-full opacity-15 blur-3xl transition-transform duration-500 group-hover:scale-125"
        style={{ backgroundColor: accent }}
      />
      <div className="relative flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-[22px] text-white shadow-xl transition-transform duration-200 group-hover:scale-105"
            style={{
              backgroundColor: accent,
              boxShadow: `0 10px 40px ${accent}30`,
            }}
          >
            {icon}
          </div>
          {selected && (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow-sm"
              style={{ color: accent }}
            >
              <CheckCircle2 className="h-4 w-4" />
              已选择
            </div>
          )}
        </div>

        <div className="mt-5">
          <h2 className="text-3xl font-black tracking-tight text-slate-950">
            {title}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">
            {description}
          </p>
        </div>

        <div className="mt-auto pt-4">
          <Button
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
            className={`w-full rounded-full px-6 py-4 text-base font-bold text-white shadow-xl transition-all duration-200 hover:-translate-y-1 ${buttonClass}`}
          >
            {buttonLabel}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </article>
  );
}
